import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeadManager from '@/components/base/HeadManager';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, comics } from '@/mocks/comics';

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'all';
  const [, setAnimateKey] = useState(0);

  const filteredComics = useMemo(() => {
    if (activeTab === 'all') return comics;
    return comics.filter((c) => c.category === activeTab);
  }, [activeTab]);

  const activeCategory = categories.find((c) => c.slug === activeTab);

  const handleTabChange = (slug: string) => {
    setSearchParams({ tab: slug });
    setAnimateKey((prev) => prev + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const categoryTitle = activeCategory ? `${activeCategory.name}漫画` : '全部漫画';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryTitle} - 香香漫画`,
    description: `香香漫画${categoryTitle}分类，免费在线阅读，下拉式追更体验。海量${categoryTitle}资源每日更新。`,
    url: `https://xiangxiangmanga.com/category?tab=${activeTab}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '香香漫画',
      url: 'https://xiangxiangmanga.com',
    },
  };

  return (
    <>
      <HeadManager
        title={`${categoryTitle} - 香香漫画分类 | 香香漫画大全免费`}
        description={`香香漫画${categoryTitle}分类，免费在线阅读，下拉式追更体验。香香漫画提供海量${categoryTitle}资源，每日更新，好看的漫画免费追更。`}
        keywords={`香香漫画,${categoryTitle},免费漫画,下拉式阅读,漫画分类`}
        canonical={`https://xiangxiangmanga.com/category?tab=${activeTab}`}
        scripts={[{ type: 'application/ld+json', content: JSON.stringify(schema) }]}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '漫画分类', href: '/category' },
                { label: categoryTitle },
              ]}
            />

            <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-4 mb-2">
              {categoryTitle}
            </h1>
            <p className="text-foreground-600 text-sm mb-6">
              共找到 <strong className="text-primary-500">{filteredComics.length}</strong> 部漫画作品
            </p>

            <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleTabChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-primary-100 hover:text-primary-600'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
                {filteredComics.map((comic) => (
                  <div key={comic.id} className="flex justify-center">
                    <ComicCard comic={comic} size="medium" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400 w-8 h-8 flex items-center justify-center"></i>
                </div>
                <p className="text-foreground-600">该分类暂无漫画，请查看其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}